import React, { useEffect, useState, useCallback } from "react";
import api, { apiError, inr, roleLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { MyCommission } from "@/components/MyCommission";
import { WalletCard } from "@/components/WalletCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { IndianRupee, Save, Plus, Users, Crown, ArrowDown } from "lucide-react";

const FIELDS = [
  { key: "seller", label: "Seller (direct)" },
  { key: "upline_total", label: "Upline total (split equally)" },
  { key: "owner", label: "Company" },
];

export default function Commission() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [cfg, setCfg] = useState(null);
  const [members, setMembers] = useState([]);
  const [sales, setSales] = useState([]);
  const [payouts, setPayouts] = useState({ payouts: [], total_unpaid: 0, total_paid: 0 });
  const [saving, setSaving] = useState(false);
  const [sale, setSale] = useState({ sale_type: "package", seller_id: "", buyer_name: "", package_name: "" });

  const load = useCallback(async () => {
    const [c, m, s, po] = await Promise.all([api.get("/commission/config"), api.get("/members"), api.get("/commission/sales").catch(() => ({ data: [] })), api.get("/commission/payouts").catch(() => ({ data: { payouts: [], total_unpaid: 0, total_paid: 0 } }))]);
    setCfg(c.data); setMembers(m.data); setSales(s.data); setPayouts(po.data);
  }, []);

  const markPaid = async (cid, pay) => {
    try {
      await api.post(`/commission/payouts/${cid}/${pay ? "pay" : "unpay"}`);
      toast.success(pay ? "Marked as paid — added to P&L expense" : "Reverted to unpaid");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [load]);

  const setFr = (k, v) => setCfg((c) => ({ ...c, franchise: { ...c.franchise, [k]: v } }));
  const setPk = (k, v) => setCfg((c) => ({ ...c, package: { ...c.package, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        franchise: { ...cfg.franchise, price: +cfg.franchise.price, seller: +cfg.franchise.seller, upline_total: +cfg.franchise.upline_total, owner: +cfg.franchise.owner, gift: +cfg.franchise.gift },
        package: { ...cfg.package, seller: +cfg.package.seller, upline_total: +cfg.package.upline_total, owner: +cfg.package.owner },
      };
      const { data } = await api.put("/commission/config", body);
      setCfg(data); toast.success("Commission plan updated — applies to all member levels");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const record = async () => {
    if (!sale.seller_id || !sale.buyer_name.trim()) { toast.error("Select the seller and enter the buyer name"); return; }
    try {
      const { data } = await api.post("/commission/sale", sale);
      toast.success(`Sale recorded — distributed ${inr(data.payouts.reduce((a, p) => a + p.amount, 0))}`);
      setSale({ sale_type: "package", seller_id: "", buyer_name: "", package_name: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const fr = cfg?.franchise, pk = cfg?.package;
  const frTotal = fr ? (+fr.seller) + (+fr.upline_total) + (+fr.owner) + (+fr.gift) : 0;

  return (
    <div>
      <PageHeader eyebrow="Business" title="Commission & Distribution" subtitle="Fixed-amount plan for franchise & package sales — editable by company/admin, applies to all member levels" />
      <div className="p-4 sm:p-8 space-y-6">
        {!isOwner && <WalletCard client={api} member />}
        {!isOwner && <MyCommission />}
        {!cfg && <p className="text-sm text-muted-foreground">Loading…</p>}
        {cfg && (
          <div className="grid lg:grid-cols-2 gap-5">
            {/* Franchise plan */}
            <Card className="p-5 bg-card/40 border-gold/30" data-testid="franchise-plan">
              <div className="flex items-center justify-between mb-1">
                <p className="font-serif font-semibold text-white flex items-center gap-2"><Crown className="w-4 h-4 text-gold" /> Franchise Sale</p>
                {isOwner && <Badge variant="outline" className="border-gold/40 text-gold text-[10px]">editable</Badge>}
              </div>
              <div className="mt-3 space-y-3">
                <NumField label="Franchise price" value={fr.price} onChange={(v) => setFr("price", v)} disabled={!isOwner} testid="fr-price" />
                {FIELDS.map((f) => <NumField key={f.key} label={f.label} value={fr[f.key]} onChange={(v) => setFr(f.key, v)} disabled={!isOwner} testid={`fr-${f.key}`} />)}
                <NumField label="Welcome gift value" value={fr.gift} onChange={(v) => setFr("gift", v)} disabled={!isOwner} testid="fr-gift" />
                <div>
                  <Label className="text-xs text-muted-foreground">Gift includes</Label>
                  <Input data-testid="fr-gift-items" value={fr.gift_items} onChange={(e) => setFr("gift_items", e.target.value)} disabled={!isOwner} className="mt-1 bg-card/60" />
                </div>
                <p className={`text-[11px] font-mono ${Math.abs(frTotal - (+fr.price)) < 0.01 ? "text-emerald-300" : "text-red-400"}`}>
                  Total distributed: {inr(frTotal)} / {inr(fr.price)}
                </p>
              </div>
            </Card>

            {/* Package plan */}
            <Card className="p-5 bg-card/40 border-gold/30" data-testid="package-plan">
              <div className="flex items-center justify-between mb-1">
                <p className="font-serif font-semibold text-white flex items-center gap-2"><Users className="w-4 h-4 text-gold" /> Package Sale</p>
                {isOwner && <Badge variant="outline" className="border-gold/40 text-gold text-[10px]">editable</Badge>}
              </div>
              <p className="text-[11px] text-muted-foreground">When a member sells an Umrah/Hajj package to a customer.</p>
              <div className="mt-3 space-y-3">
                {FIELDS.map((f) => <NumField key={f.key} label={f.label} value={pk[f.key]} onChange={(v) => setPk(f.key, v)} disabled={!isOwner} testid={`pk-${f.key}`} />)}
                <p className="text-[11px] text-muted-foreground">Upline total is divided equally among every member above the seller (up to just below the company). Package cost & price are set in <span className="text-gold">Package Pricing</span>.</p>
              </div>
            </Card>
          </div>
        )}
        {cfg && isOwner && (
          <Button data-testid="save-commission-btn" onClick={save} disabled={saving} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Commission Plan"}</Button>
        )}

        {/* Record a sale */}
        {isOwner && (
          <Card className="p-5 bg-card/40 border-border/60" data-testid="record-sale">
            <p className="font-serif font-semibold text-white flex items-center gap-2 mb-3"><Plus className="w-4 h-4 text-gold" /> Record a Sale</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Sale type</Label>
                <Select value={sale.sale_type} onValueChange={(v) => setSale({ ...sale, sale_type: v })}>
                  <SelectTrigger data-testid="sale-type-select" className="mt-1 bg-card/60"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="package">Package</SelectItem>
                    <SelectItem value="franchise">Franchise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Sold by (member)</Label>
                <Select value={sale.seller_id} onValueChange={(v) => setSale({ ...sale, seller_id: v })}>
                  <SelectTrigger data-testid="sale-seller-select" className="mt-1 bg-card/60"><SelectValue placeholder="Select member" /></SelectTrigger>
                  <SelectContent>
                    {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {roleLabel(m.role)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-muted-foreground">Buyer / customer name</Label><Input data-testid="sale-buyer-input" value={sale.buyer_name} onChange={(e) => setSale({ ...sale, buyer_name: e.target.value })} className="mt-1 bg-card/60" /></div>
              <div><Label className="text-xs text-muted-foreground">Package (optional)</Label><Input data-testid="sale-package-input" value={sale.package_name} onChange={(e) => setSale({ ...sale, package_name: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. Deluxe Umrah" /></div>
            </div>
            <Button data-testid="record-sale-btn" onClick={record} className="mt-3 bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><IndianRupee className="w-4 h-4" /> Record & Distribute</Button>
          </Card>
        )}

        {/* Sales history */}
        <div>
          <p className="eyebrow text-gold mb-3">Recent Sales & Distribution</p>
          {sales.length === 0 && <p className="text-sm text-muted-foreground">No sales recorded yet.</p>}
          <div className="space-y-3">
            {sales.map((s) => (
              <Card key={s.id} data-testid={`sale-card-${s.id}`} className="p-4 bg-card/40 border-border/60">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-serif font-semibold text-white">{s.buyer_name} <Badge variant="outline" className="ml-2 border-gold/40 text-gold text-[9px] capitalize">{s.sale_type}</Badge></p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Sold by {s.seller_name}{s.package_name ? ` · ${s.package_name}` : ""}{s.gift ? ` · gift ${inr(s.gift)}` : ""}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {s.payouts.map((p, i) => (
                    <React.Fragment key={i}>
                      <div className="px-2.5 py-1.5 rounded-lg bg-primary/40 border border-border/50 text-center">
                        <p className="text-[10px] text-muted-foreground capitalize">{roleLabel(p.level)}</p>
                        <p className="text-xs font-serif text-white">{p.name}</p>
                        <p className="text-[11px] font-mono text-gold">{inr(p.amount)}</p>
                      </div>
                      {i < s.payouts.length - 1 && <ArrowDown className="w-3 h-3 text-gold/40 rotate-[-90deg]" />}
                    </React.Fragment>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Payouts — owner/admin only */}
      {isOwner && (
        <div className="px-4 sm:px-8 pb-8">
          <div className="flex items-center justify-between mb-3">
            <p className="eyebrow text-gold">Member Commission Payouts</p>
            <div className="flex gap-4 text-[11px] font-mono">
              <span className="text-amber-300">Unpaid {inr(payouts.total_unpaid)}</span>
              <span className="text-emerald-300">Paid {inr(payouts.total_paid)}</span>
            </div>
          </div>
          {payouts.payouts.length === 0 && <p className="text-sm text-muted-foreground">No commissions yet.</p>}
          <div className="space-y-2">
            {payouts.payouts.map((p) => (
              <Card key={p.id} data-testid={`payout-${p.id}`} className="p-3 bg-card/40 border-border/60 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-white font-medium">{p.member_name} <span className="text-muted-foreground text-[10px] capitalize">· {roleLabel(p.level)} · {p.sale_type}</span></p>
                  <p className="text-[10px] text-muted-foreground">{p.paid_at ? `Paid ${new Date(p.paid_at).toLocaleDateString("en-IN")}` : "Unpaid"}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <p className="font-mono text-gold text-sm">{inr(p.amount)}</p>
                  {p.status === "paid" ? (
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">Paid</Badge>
                  ) : (
                    <Badge variant="outline" className="border-amber-500/40 text-amber-300">Unpaid</Badge>
                  )}
                  <Button size="sm" data-testid={`payout-toggle-${p.id}`} onClick={() => markPaid(p.id, p.status !== "paid")}
                    className={p.status === "paid" ? "bg-transparent border border-border/60 text-muted-foreground hover:bg-card/60 text-[10px] h-7" : "bg-gold text-[#0a2e22] hover:bg-gold/90 text-[10px] h-7 font-semibold"}>
                    {p.status === "paid" ? "Revert" : "Mark Paid"}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-3">Marking a commission as Paid records it as an expense in Profit &amp; Loss, reducing company net profit. Visible to company/admin only.</p>
        </div>
      )}
    </div>
  );
}

function NumField({ label, value, onChange, disabled, testid }) {
  return (
    <div className="flex items-center gap-3">
      <Label className="text-xs text-muted-foreground w-44 flex-shrink-0">{label}</Label>
      <div className="relative flex-1">
        <IndianRupee className="w-3.5 h-3.5 text-gold absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid={testid} type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="bg-card/60 pl-8" />
      </div>
    </div>
  );
}
